import React, { useState, useMemo, useEffect } from 'react';
import clsx from 'clsx';
import { LuArrowLeft, LuArrowRight } from 'react-icons/lu';
import { useGetPhonicsLessonsQuery } from '@/api/types';
import {
  PhonicsLessonGroup,
  PhonicsLesson,
  PhonicsActivity,
  PhonicsClassStatus,
} from '@/api/types';
import { useData } from '@/context/Data';
import { useUi } from '@/context/Ui';
import { Button, DefinitionList, Confirmation } from '@/components';
import { PhonicsLessonPlayerKind } from '../LessonPlayer/types';
import { ManageBreadcrumb } from './ManageBreadcrumb';
import { ManageLessonLayout } from './ManageLessonLayout';
import { ManageActivityList } from './ManageActivityList';
import { ManagePanel } from './ManagePanel';
import { ManageLaunchLessonLink } from './ManageLaunchLessonLink';
import { ManageDecodableBookPod } from './ManageDecodableBookPod';
import { ManageDecodableBooksNone } from './ManageDecodableBooksNone';
import { ManageLessonMediaAudit } from './ManageLessonMediaAudit';

interface ManageLessonProps {
  lesson: PhonicsLesson;
  phonicsClass: PhonicsClassStatus;
  onLaunchLesson: (
    lesson: PhonicsLesson,
    kind: PhonicsLessonPlayerKind,
  ) => void;
  onViewResults: (lesson: PhonicsLesson) => void;
  onPreviewLesson: (lesson: PhonicsLesson) => void;
  onCompleteLesson: (
    lesson: PhonicsLesson,
    onComplete?: () => void,
    onError?: (errorMessage: string) => void,
  ) => void;
  completeLessonLoading?: boolean;
  onPreviewActivity: (lesson: PhonicsLesson, activity: PhonicsActivity) => void;
  onPrevLesson?: (prevLesson?: PhonicsLesson) => void;
  onNextLesson?: (nextLesson?: PhonicsLesson) => void;
  onGetYearGroup: (yearGroupId: string) => PhonicsLessonGroup | undefined;
}

export const ManageLesson = ({
  lesson,
  phonicsClass,
  onLaunchLesson,
  onViewResults,
  onPreviewLesson,
  onCompleteLesson,
  completeLessonLoading = false,
  onPreviewActivity,
  onPrevLesson,
  onNextLesson,
  onGetYearGroup,
}: ManageLessonProps) => {
  const { currentClass } = useData();
  const { sendAlert } = useUi();

  const { data: lessonsResult } = useGetPhonicsLessonsQuery({
    fetchPolicy: 'cache-first',
  });

  const [tryCompleteLesson, setTryCompleteLesson] = useState(false);

  const [completeLessonErrror, setCompleteLessonError] = useState<
    string | undefined
  >();

  const [activityDetail, setActivityDetail] = useState<
    PhonicsActivity | undefined
  >(undefined);

  const [groupActivities, setGroupActivities] = useState<
    Array<PhonicsActivity>
  >([]);

  const [individualActivities, setIndividualActivities] = useState<
    Array<PhonicsActivity>
  >([]);

  const currentIndex = useMemo(() => {
    if (!lessonsResult?.getPhonicsLessons || !lesson) {
      return -1;
    }

    return lessonsResult.getPhonicsLessons.findIndex(
      (lessonItem) => lessonItem.id === lesson.id,
    );
  }, [lessonsResult, lesson]);

  const prevLesson = useMemo(() => {
    if (!lessonsResult?.getPhonicsLessons || currentIndex === -1) {
      return undefined;
    }

    return lessonsResult.getPhonicsLessons[currentIndex - 1];
  }, [lessonsResult, currentIndex]);

  const nextLesson = useMemo(() => {
    if (!lessonsResult?.getPhonicsLessons || currentIndex === -1) {
      return undefined;
    }

    return lessonsResult.getPhonicsLessons[currentIndex + 1];
  }, [lessonsResult, currentIndex]);

  useEffect(() => {
    if (!lesson) {
      return;
    }

    setGroupActivities(
      lesson.activities.filter((activity) => activity.type.mode === 'TEACH'),
    );

    setIndividualActivities(
      lesson.activities.filter((activity) => activity.type.mode === 'LEARN'),
    );
  }, [lesson]);

  return lesson ? (
    <div className="flex flex-col gap-6">
      <ManageBreadcrumb
        section="lessons"
        weekGroup={lesson.group || undefined}
        lesson={lesson}
        className="mb-3"
        suffix={<strong className="font-bold">Plan</strong>}
      />

      <ManageLessonLayout
        lesson={lesson}
        phonicsClass={phonicsClass}
        onGetYearGroup={onGetYearGroup}
      >
        <div className="flex gap-3 justify-between">
          <div className="flex gap-3">
            <Button
              style="secondary"
              onClick={() => onPreviewLesson(lesson)}
              lessPadding
              tooltipText="Preview lesson"
              tooltipPosition="bottom"
              ariaLabel="Preview lesson"
            >
              Preview Lesson
            </Button>

            <ManageLaunchLessonLink
              lesson={lesson}
              phonicsClass={phonicsClass}
              kind="reading"
              label="Launch Reading"
              onLaunchLesson={onLaunchLesson}
              tooltipPosition="bottom"
            />

            <ManageLaunchLessonLink
              lesson={lesson}
              phonicsClass={phonicsClass}
              kind="spelling"
              label="Launch Spelling"
              onLaunchLesson={onLaunchLesson}
              tooltipPosition="bottom"
            />

            <Button
              onClick={() => onViewResults(lesson)}
              lessPadding
              tooltipText="View class results for this lesson"
              tooltipPosition="bottom"
              ariaLabel="View class results for this lesson"
            >
              View Results
            </Button>

            <Button
              onClick={() => setTryCompleteLesson(true)}
              lessPadding
              tooltipText="Mark this lesson as complete"
              tooltipPosition="bottom"
              ariaLabel="Mark this lesson as complete"
            >
              Mark as complete
            </Button>
          </div>

          <div className="flex gap-3">
            {onPrevLesson && (
              <Button
                style="secondary"
                onClick={() => onPrevLesson(prevLesson)}
                disabled={!prevLesson}
                tooltipText="Previous lesson"
                tooltipPosition="bottom"
                ariaLabel="Previous lesson"
              >
                <LuArrowLeft />
              </Button>
            )}

            {onNextLesson && (
              <Button
                style="secondary"
                onClick={() => onNextLesson(nextLesson)}
                disabled={!nextLesson}
                tooltipText="Previous lesson"
                tooltipPosition="bottom"
                ariaLabel="Previous lesson"
              >
                <LuArrowRight />
              </Button>
            )}
          </div>
        </div>

        <ManagePanel title="Activities" yOrientation bgColorScheme="white">
          <div className="relative">
            <div
              className={clsx(
                'transition-transform duration-300 flex flex-col gap-6 w-1/2 pr-2',
              )}
            >
              <ManagePanel
                title="Reading Activities"
                yOrientation
                titleWithoutUnderline
              >
                <ManageActivityList
                  viewAs="grid"
                  activities={groupActivities}
                  selectedActivity={activityDetail}
                  onSelectActivity={setActivityDetail}
                  inverted
                />
              </ManagePanel>

              <ManagePanel
                title="Spelling Activities"
                yOrientation
                titleWithoutUnderline
              >
                <ManageActivityList
                  viewAs="grid"
                  activities={individualActivities}
                  selectedActivity={activityDetail}
                  onSelectActivity={setActivityDetail}
                  inverted
                />
              </ManagePanel>
            </div>

            <div
              className={clsx(
                'w-1/2 h-full text-green-dark px-6',
                'absolute top-0 right-0 transition-transform duration-300',
              )}
            >
              {activityDetail && (
                <div className="flex flex-col gap-6">
                  <h2 className="text-xl font-bold text-green-darker">
                    Activity Detail: {activityDetail.type.name}
                  </h2>

                  {activityDetail.title && <p>{activityDetail.title}</p>}

                  <DefinitionList
                    items={{
                      ...(activityDetail.sounds && activityDetail.sounds.length
                        ? {
                            Sounds: activityDetail.sounds,
                          }
                        : {}),
                      ...(activityDetail.words && activityDetail.words.length
                        ? {
                            Words: activityDetail.words.join(', '),
                          }
                        : {}),
                      ...(activityDetail.words2 && activityDetail.words2.length
                        ? {
                            'Alt. words': activityDetail.words2.join(', '),
                          }
                        : {}),
                      ...(activityDetail.wordSegmentations &&
                      activityDetail.wordSegmentations.length
                        ? {
                            Segmentations:
                              activityDetail.wordSegmentations.join(', '),
                          }
                        : {}),
                      ...(activityDetail.sentences &&
                      activityDetail.sentences.length
                        ? {
                            Sentences: activityDetail.sentences.join(', '),
                          }
                        : {}),
                    }}
                  />

                  {activityDetail.description}

                  <div>
                    <Button
                      onClick={() => onPreviewActivity(lesson, activityDetail)}
                    >
                      Preview Activity
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </ManagePanel>

        <ManagePanel title="Decodable Books" yOrientation bgColorScheme="white">
          {lesson.decodableBookIds?.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
              {lesson.decodableBookIds?.map((bookId) => (
                <ManageDecodableBookPod key={bookId} bookId={bookId} />
              ))}
            </div>
          ) : (
            <ManageDecodableBooksNone />
          )}
        </ManagePanel>

        <ManageLessonMediaAudit lesson={lesson} />
      </ManageLessonLayout>

      <Confirmation
        visible={tryCompleteLesson}
        title="Complete lesson"
        message={`Are you sure you want to complete lesson ${lesson.number} for class ${currentClass?.label}?`}
        errorMessage={completeLessonErrror}
        onConfirm={async () => {
          onCompleteLesson(
            lesson,
            () => {
              setTryCompleteLesson(false);
              setCompleteLessonError(undefined);
              sendAlert(
                `Lesson ${lesson.number} was completed for class ${currentClass?.label}`,
                {
                  type: 'success',
                  title: 'Lesson Completed',
                },
              );
            },
            () => {
              setCompleteLessonError(
                'There was an error completing the lesson.',
              );
            },
          );
        }}
        loading={completeLessonLoading}
        onCancel={() => {
          setTryCompleteLesson(false);
          setCompleteLessonError(undefined);
        }}
      />
    </div>
  ) : null;
};
